/**
 * Polish layer — 3-layer feedback rule (DEC-005 §Décision).
 *
 * Le shell applique uniformément feedback (visual + audio + haptic) sur :
 *   - TRIAL_COMPLETED  → micro feedback selon correct/incorrect
 *   - STREAK_REACHED   → +3 correct consécutif (sparkle ocre)
 *   - PB_BEATEN        → personal best (halo doré)
 *
 * Les jeux n'ont **pas** à coder le feedback — uniquement présenter le stimulus.
 * Anti-divergence cross-jeux : tous les jeux feel "Clairnaube" identique.
 *
 * Routing audio par maître (DEC-005 + voix Vesperin) :
 *   - air     → cloches bambou (Inori)
 *   - feu     → forge braise (Embra)
 *   - eau     → goutte cristalline (Ondine)
 *   - terre   → tambour sourd (Garek)
 *   - foudre  → crépitement bref (Volta)
 *   - lumiere → harpe arpège (Sole)
 *
 * Sprint 2.0 : Tone.js (lazy) + Capacitor.Haptics natif iOS/Android + web fallback.
 *
 * Réf timing : design-system motion.feedbackVisualMs / Audio / Haptic.
 */
import { createToneEngine, playPBStinger, playSignatureSound, playStreakStinger, } from './audio';
import { createHapticEngine } from './haptic/haptic-engine';
const DEFAULT_OPTIONS = {
    enableVisual: true,
    enableAudio: true,
    enableHaptic: true,
    reducedMotion: false,
    gameMaster: 'air',
};
/**
 * Singleton engines partagés cross-jeux. Une seule instance Tone (donc un seul
 * AudioContext) et un seul HapticEngine pour toute l'app, peu importe combien
 * de jeux sont chargés.
 */
let sharedToneEngine = null;
let sharedHapticEngine = null;
function getToneEngine() {
    if (!sharedToneEngine)
        sharedToneEngine = createToneEngine();
    return sharedToneEngine;
}
function getHapticEngine() {
    if (!sharedHapticEngine)
        sharedHapticEngine = createHapticEngine();
    return sharedHapticEngine;
}
/**
 * Construit la couche polish. Le visual feedback est délégué au render adapter
 * du jeu (showFeedback) : le shell décide *quand* appeler, le jeu décide *comment* afficher.
 */
export function createPolishLayer(adapter, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const tone = getToneEngine();
    const haptic = getHapticEngine();
    return {
        async applyTrialFeedback(correct, feedbackOptions) {
            const tasks = [];
            if (opts.enableVisual) {
                tasks.push(Promise.resolve(adapter.showFeedback(correct, feedbackOptions)));
            }
            if (opts.enableAudio) {
                tasks.push(playTrialSound(tone, opts.gameMaster, correct));
            }
            if (opts.enableHaptic && !opts.reducedMotion) {
                // Voix Vesperin sober : light pour les deux. PAS de medium sur incorrect (anti-FOMO).
                tasks.push(haptic.impact('light'));
            }
            await Promise.all(tasks);
        },
        async applyStreakFeedback(streakCount) {
            if (streakCount < 3)
                return;
            const tasks = [];
            if (opts.enableAudio) {
                tasks.push(initThen(tone, () => playStreakStinger(tone)));
            }
            if (opts.enableHaptic && !opts.reducedMotion) {
                tasks.push(haptic.impact('medium'));
            }
            await Promise.all(tasks);
        },
        async applyPBFeedback() {
            const tasks = [];
            if (opts.enableAudio) {
                tasks.push(initThen(tone, () => playPBStinger(tone)));
            }
            if (opts.enableHaptic && !opts.reducedMotion) {
                tasks.push(haptic.impact('heavy'));
            }
            await Promise.all(tasks);
        },
    };
}
async function playTrialSound(tone, master, correct) {
    await tone.init();
    if (correct) {
        await playSignatureSound(tone, master);
    }
    else {
        await playSignatureSound(tone, master, {
            pitch: -3,
            filterCutoff: 1500,
            gain: 0.7,
        });
    }
}
async function initThen(tone, fn) {
    await tone.init();
    await fn();
}
/** Helper test : reset les singletons (à appeler dans afterEach des tests engines). */
export function __resetPolishSingletonsForTests() {
    sharedToneEngine?.dispose();
    sharedToneEngine = null;
    sharedHapticEngine = null;
}
//# sourceMappingURL=polish.js.map